/**
 * Reporting Central — how a cell the server sent becomes what a person reads.
 *
 * The server hands every cell over as JSON: a number arrives as a plain string
 * ("10000.00") so no precision is lost on the way, a date arrives as
 * YYYY-MM-DD, and an empty cell arrives as null. Nothing here fetches, decides
 * or rounds business figures — it only presents the value the server settled on.
 *
 * Pure functions on purpose: the table, the totals row and the tests all read
 * the same rules, so a column can never be formatted two different ways.
 */

// The column kinds that belong on the RIGHT of their column.
const NUMERIC_KINDS = ['integer', 'number', 'price', 'money'];

export const isNumericKind = (kind) => NUMERIC_KINDS.includes(kind);

// Thousands separators, applied to the whole part only.
const withThousands = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * `value` shown with exactly `decimals` decimal places and thousands
 * separators. Anything that is not a finite number is handed back untouched
 * rather than shown as "NaN" — a cell we cannot read is still the server's
 * word, and inventing a figure is worse than showing the raw one.
 */
const grouped = (value, decimals) => {
  const num = Number(value);
  if (value === '' || !Number.isFinite(num)) return String(value);
  const fixed = num.toFixed(decimals);
  const negative = fixed.startsWith('-');
  const [whole, fraction] = fixed.replace('-', '').split('.');
  const text = withThousands(whole) + (fraction ? `.${fraction}` : '');
  return negative ? `-${text}` : text;
};

/**
 * A price shows at least 2 decimals and at most 4 — the number of decimals the
 * server actually sent, once trailing zeros past the second are dropped. So
 * "10.5000" reads 10.50 and "10.1234" keeps all four.
 */
const priceDecimals = (value) => {
  const text = String(value);
  const dot = text.indexOf('.');
  if (dot === -1) return 2;
  const meaningful = text.slice(dot + 1).replace(/0+$/, '').length;
  return Math.min(4, Math.max(2, meaningful));
};

/**
 * A date the server sent (YYYY-MM-DD, or a full timestamp) as dd/mm/yyyy.
 * Parsed from the text rather than through `new Date`, so a timestamp is never
 * quietly shifted into the reader's own time zone.
 */
export const formatDate = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!match) return String(value);
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

/** A server timestamp as dd/mm/yyyy hh:mm — used for "figures as at …". */
export const formatDateTime = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(value));
  if (!match) return formatDate(value);
  const [, year, month, day, hour, minute] = match;
  return `${day}/${month}/${year} ${hour}:${minute}`;
};

/** One cell, formatted for its column kind. A null cell is an empty cell. */
export const formatCell = (value, kind) => {
  if (value === null || value === undefined) return '';
  switch (kind) {
    case 'date':
      return formatDate(value);
    case 'integer':
      return grouped(value, 0);
    case 'number':
    case 'money':
      return grouped(value, 2);
    case 'price':
      return grouped(value, priceDecimals(value));
    default:
      return String(value);
  }
};

export default formatCell;
